import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCaregiver } from '../../hooks/useCaregiver';

/**
 * Per-caregiver banners surfacing SMS delivery problems that are otherwise
 * silent (the caregiver just stops getting alerts).
 *
 * Two independent conditions, both read from the `caregivers` row:
 *
 *   - `sms_opted_out === true`: caregiver replied STOP to AidFone messages
 *     (Twilio 21610). Fix is one SMS reply ("START") from the caregiver's
 *     phone to AidFone's Twilio number. No in-dashboard action can do it for
 *     them — the carrier needs the START to come from their handset.
 *
 *   - `phone == null && !sms_opted_out`: their stored phone number bounced
 *     (Twilio 21614), so the server nulled it. Fix is to add a working
 *     mobile number in account settings.
 *
 * Both banners are PER-CAREGIVER, not per-senior — the underlying flag is
 * on `caregivers`, not `seniors`. Rendered once above the senior cards.
 * Persistent until resolved (no dismiss control) because the resolution
 * itself clears the flag on the next successful send / settings update.
 *
 * Visual treatment matches [ScamBlockingInactiveBanner] (amber gradient,
 * rounded-2xl, icon-left). Tone is "fix this when you can" — these aren't
 * emergencies, they're safety-net holes the caregiver chose (STOP reply)
 * or that surfaced through a data entry error (bad phone).
 */
const AIDFONE_TWILIO_FROM_NUMBER = '+14503679692';
const AIDFONE_TWILIO_FROM_DISPLAY = '+1 (450) 367-9692';

export const CaregiverSmsBanners: FC = () => {
  const { caregiver, loading } = useCaregiver();
  const { t } = useTranslation();

  if (loading || !caregiver) return null;

  const isOptedOut = caregiver.sms_opted_out === true;
  const isPhoneMissing = !isOptedOut && (caregiver.phone == null || caregiver.phone === '');

  if (!isOptedOut && !isPhoneMissing) return null;

  return (
    <div className="flex flex-col gap-3 mb-4">
      {/* OPT-OUT BANNER — shown on top when both fire because it's more
          urgent (one SMS reply fixes it; no_phone needs a settings visit). */}
      {isOptedOut && (
        <div
          role="alert"
          className="flex items-start gap-4 p-4 rounded-2xl border border-amber-300"
          style={{
            background:
              'linear-gradient(135deg, rgba(254, 215, 170, 0.85) 0%, rgba(254, 243, 199, 0.85) 100%)',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-amber-800" style={{ fontSize: '22px' }}>
              notifications_off
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-display font-bold text-amber-900">
              {t('banners.smsOptedOut.title')}
            </h3>
            <p className="text-xs text-amber-900/80 mt-0.5 leading-relaxed">
              {t('banners.smsOptedOut.body')}{' '}
              <a
                href={`sms:${AIDFONE_TWILIO_FROM_NUMBER}?body=START`}
                className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700 whitespace-nowrap"
              >
                {AIDFONE_TWILIO_FROM_DISPLAY}
              </a>
              .
            </p>
          </div>
        </div>
      )}

      {/* NO-PHONE BANNER — softer amber to differentiate from opt-out and
          because it's typically a one-time setup issue, not an action the
          caregiver took. Right-aligned CTA jumps straight to settings. */}
      {isPhoneMissing && (
        <div
          role="alert"
          className="flex items-start gap-4 p-4 rounded-2xl border border-amber-200"
          style={{
            background:
              'linear-gradient(135deg, rgba(254, 243, 199, 0.9) 0%, rgba(254, 249, 231, 0.9) 100%)',
          }}
        >
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-amber-700" style={{ fontSize: '22px' }}>
              phone_disabled
            </span>
          </div>
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-display font-bold text-amber-900">
                {t('banners.smsNoPhone.title')}
              </h3>
              <p className="text-xs text-amber-900/80 mt-0.5 leading-relaxed">
                {t('banners.smsNoPhone.body')}
              </p>
            </div>
            <Link
              to="/account"
              className="mt-2 sm:mt-0 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-semibold transition-colors whitespace-nowrap min-h-[44px] sm:min-h-0"
            >
              {t('banners.smsNoPhone.cta')}
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                arrow_forward
              </span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaregiverSmsBanners;
